/**
 * Centralized constants for authentication-related messages
 * This file contains all user-facing text to support future internationalization
 */

export const AUTH_MESSAGES = {
  // Login Messages
  LOGIN: {
    INVALID_CREDENTIALS: 'Invalid email or password. Please try again.',
    ACCOUNT_INACTIVE: 'Your account is inactive. Please contact support.',
    ACCOUNT_LOCKED: 'Your account has been temporarily locked due to too many failed attempts.',
    FIELDS_REQUIRED: 'Email and password are required.',
    SUCCESS: 'Login successful. Welcome back!',
    LOADING: 'Signing you in...',
  },

  // Registration Messages
  REGISTER: {
    SUCCESS: 'Account created successfully. Welcome!',
    EMAIL_EXISTS: 'An account with this email already exists.',
    WEAK_PASSWORD: 'Password does not meet security requirements.',
    FIELDS_REQUIRED: 'All fields are required.',
    LOADING: 'Creating your account...',
    FAILED: 'Registration failed. Please try again.',
  },

  // Password Reset Messages
  PASSWORD_RESET: {
    EMAIL_SENT: 'Password reset instructions have been sent to your email.',
    EMAIL_NOT_FOUND: 'No account found with that email address.',
    INVALID_EMAIL: 'Please enter a valid email address.',
    SENDING: 'Sending reset email...',
    SEND_FAILED: 'Failed to send reset email. Please try again.',
    
    // Token validation
    TOKEN_INVALID: 'This reset link is invalid. Please request a new one.',
    TOKEN_EXPIRED: 'This reset link has expired. Please request a new one.',
    TOKEN_USED: 'This reset link has already been used. Please request a new one.',
    VALIDATING_TOKEN: 'Verifying reset link...',
    
    // Password update
    PASSWORD_UPDATED: 'Password updated successfully. You can now log in with your new password.',
    PASSWORDS_DONT_MATCH: 'Passwords do not match.',
    FIELDS_REQUIRED: 'Please fill in all fields.',
    UPDATING_PASSWORD: 'Updating password...',
    UPDATE_FAILED: 'Failed to update password. Please try again.',
    WEAK_PASSWORD: 'Password is too weak. Please choose a stronger password.',
    SAME_PASSWORD: 'New password cannot be the same as your current password.',
    
    // UI Text
    PROMPT_TEXT: 'Having trouble logging in?',
    PROMPT_LINK: 'Reset your password',
    MODAL_TITLE: 'Reset Password',
    MODAL_DESCRIPTION: 'Enter your email address and we\'ll send you instructions to reset your password.',
    CONFIRM_TITLE: 'Reset Your Password',
    CONFIRM_DESCRIPTION: 'Enter your new password below',
    SUCCESS_TITLE: 'Password Reset Successfully',
    SUCCESS_DESCRIPTION: 'Your password has been updated. You can now log in with your new password.',
    EMAIL_SENT_TITLE: 'Check Your Email',
  },

  // 2FA Messages
  TWO_FACTOR: {
    VERIFICATION_FAILED: '2FA verification failed. Please try again.',
    INVALID_CODE: 'Invalid verification code. Please try again.',
    EXPIRED_CODE: 'Verification code has expired. Please generate a new one.',
    SETUP_SUCCESS: '2FA has been successfully enabled for your account.',
    DISABLE_SUCCESS: '2FA has been disabled for your account.',
    BACKUP_CODES_GENERATED: 'New backup codes have been generated.',
    LOADING_SETUP: 'Setting up 2FA...',
    LOADING_VERIFY: 'Verifying code...',
    LOADING_DISABLE: 'Disabling 2FA...',
  },

  // Rate Limiting Messages
  RATE_LIMIT: {
    TOO_MANY_REQUESTS: 'Too many requests. Please try again later.',
    RETRY_AFTER: (seconds: number) => `Too many requests. Please try again in ${seconds} seconds.`,
    COUNTDOWN: (seconds: number) => `Please wait ${seconds} seconds before trying again.`,
  },

  // General Error Messages
  ERRORS: {
    NETWORK: 'Network error. Please check your connection.',
    SERVER: 'Server error. Please try again later.',
    UNKNOWN: 'An unexpected error occurred. Please try again.',
    UNAUTHORIZED: 'You are not authorized to perform this action.',
    FORBIDDEN: 'Access denied.',
    VALIDATION: 'Please check your input and try again.',
    SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  },

  // Success Messages
  SUCCESS: {
    LOGOUT: 'You have been logged out successfully.',
    PASSWORD_CHANGED: 'Password changed successfully.',
    PROFILE_UPDATED: 'Profile updated successfully.',
    EMAIL_VERIFIED: 'Email verified successfully.',
  },

  // Loading Messages
  LOADING: {
    DEFAULT: 'Loading...',
    AUTHENTICATING: 'Authenticating...',
    SAVING: 'Saving...',
    UPDATING: 'Updating...',
    DELETING: 'Deleting...',
    VERIFYING: 'Verifying...',
    PROCESSING: 'Processing...',
  },

  // Validation Messages
  VALIDATION: {
    EMAIL_REQUIRED: 'Email is required.',
    EMAIL_INVALID: 'Please enter a valid email address.',
    PASSWORD_REQUIRED: 'Password is required.',
    PASSWORD_TOO_SHORT: 'Password must be at least 8 characters long.',
    PASSWORD_TOO_WEAK: 'Password must include uppercase, lowercase, numbers, and special characters.',
    CONFIRM_PASSWORD_REQUIRED: 'Please confirm your password.',
    PASSWORDS_MUST_MATCH: 'Passwords must match.',
    FIRST_NAME_REQUIRED: 'First name is required.',
    LAST_NAME_REQUIRED: 'Last name is required.',
    CURRENT_PASSWORD_REQUIRED: 'Current password is required.',
    CURRENT_PASSWORD_INCORRECT: 'Current password is incorrect.',
  },

  // Button Text
  BUTTONS: {
    LOGIN: 'Sign In',
    REGISTER: 'Create Account',
    CREATE_ONE: 'Create one',
    LOGOUT: 'Sign Out',
    SEND_RESET_EMAIL: 'Send Reset Email',
    UPDATE_PASSWORD: 'Update Password',
    RESET_PASSWORD: 'Reset Password',
    CANCEL: 'Cancel',
    CONTINUE: 'Continue',
    BACK_TO_LOGIN: 'Back to Login',
    DONE: 'Done',
    SAVE: 'Save',
    VERIFY: 'Verify',
    ENABLE_2FA: 'Enable 2FA',
    DISABLE_2FA: 'Disable 2FA',
    GENERATE_CODES: 'Generate New Backup Codes',
    TRY_AGAIN: 'Try Again',
    CLOSE: 'Close',
  },

  // Form Labels
  LABELS: {
    EMAIL: 'Email Address',
    PASSWORD: 'Password',
    CURRENT_PASSWORD: 'Current Password',
    NEW_PASSWORD: 'New Password',
    CONFIRM_PASSWORD: 'Confirm Password',
    FIRST_NAME: 'First Name',
    LAST_NAME: 'Last Name',
    VERIFICATION_CODE: '6-Digit Code',
    BACKUP_CODE: 'Backup Code',
  },

  // Placeholders
  PLACEHOLDERS: {
    EMAIL: 'Enter your email address',
    PASSWORD: 'Enter your password',
    CURRENT_PASSWORD: 'Enter your current password',
    NEW_PASSWORD: 'Enter your new password',
    CONFIRM_PASSWORD: 'Confirm your new password',
    FIRST_NAME: 'Enter your first name',
    LAST_NAME: 'Enter your last name',
    VERIFICATION_CODE: 'Enter 6-digit code',
    BACKUP_CODE: 'Enter backup code',
  },

  // Security Messages
  SECURITY: {
    PASSWORD_REQUIREMENTS_TITLE: 'Password Requirements',
    PASSWORD_REQUIREMENTS: [
      'At least 8 characters long',
      'Contains uppercase letter (A-Z)',
      'Contains lowercase letter (a-z)',
      'Contains number (0-9)',
      'Contains special character (!@#$%^&*)',
    ],
    TOKEN_SECURITY_NOTICE: 'This link is secure and will expire for your protection.',
    TWO_FACTOR_NOTICE: 'Two-factor authentication adds an extra layer of security to your account.',
    LOGOUT_OTHER_SESSIONS: 'Sign out of all other devices',
    SESSION_SECURITY: 'For your security, you will be automatically signed out after periods of inactivity.',
  },

  // Help Messages
  HELP: {
    FORGOT_PASSWORD: 'Forgot your password? Use the reset link to create a new one.',
    TWO_FACTOR_HELP: 'Use an authenticator app like Google Authenticator or Authy to generate verification codes.',
    BACKUP_CODES_HELP: 'Save these backup codes in a secure location. Each code can only be used once.',
    CONTACT_SUPPORT: 'Need help? Contact our support team.',
  },
} as const;

// Type for accessing nested message keys
export type AuthMessageKey = keyof typeof AUTH_MESSAGES;
export type LoginMessageKey = keyof typeof AUTH_MESSAGES.LOGIN;
export type RegisterMessageKey = keyof typeof AUTH_MESSAGES.REGISTER;
export type PasswordResetMessageKey = keyof typeof AUTH_MESSAGES.PASSWORD_RESET;
export type TwoFactorMessageKey = keyof typeof AUTH_MESSAGES.TWO_FACTOR;
export type ErrorMessageKey = keyof typeof AUTH_MESSAGES.ERRORS;

/**
 * Helper function to get nested message values
 */
export function getAuthMessage<T extends keyof typeof AUTH_MESSAGES>(
  category: T,
  key: keyof typeof AUTH_MESSAGES[T]
): string {
  const message = AUTH_MESSAGES[category][key];
  return typeof message === 'function' ? message.toString() : message as string;
}

/**
 * Helper function for rate limit messages with dynamic values
 */
export function getRateLimitMessage(seconds: number, type: 'retry' | 'countdown' = 'retry'): string {
  if (type === 'retry') {
    return AUTH_MESSAGES.RATE_LIMIT.RETRY_AFTER(seconds);
  }
  return AUTH_MESSAGES.RATE_LIMIT.COUNTDOWN(seconds);
}